import { tr } from '@/lib/i18n-core';
import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { CalendarOff, UserRound } from 'lucide-react';
import { post } from '@/lib/api';
import { describeError, useAuth } from '@/lib/auth';
import { humanize, todayIso } from '@/lib/utils';
import { FormActions, FormScreen, FormSection } from '@/components/ui/form-screen';
import { Alert, DatePicker, Field, Select, Textarea } from '@/components/ui/primitives';
import { useToast } from '@/components/ui/toast';
import { safeReturnTo } from '../opd/BookAppointmentPage';
import { HR_TAB_PATH, useLeaveEmployeeOptions } from './employee-options';

export function RequestLeavePage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();
  const returnTo = safeReturnTo(params.get('returnTo'), HR_TAB_PATH.leave);
  const { can } = useAuth();
  const [chosenEmployeeId, setEmployeeId] = useState('');
  const [type, setType] = useState('casual');
  const [fromDate, setFromDate] = useState(todayIso());
  const [toDate, setToDate] = useState(todayIso());
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  const employees = useLeaveEmployeeOptions(can('hr.employee.manage'));
  const employeeId = chosenEmployeeId || (employees.ownOnly ? employees.data?.[0]?.id ?? '' : '');

  const request = useMutation({
    mutationFn: () => post('/hr/leave', { employeeId, type, fromDate, toDate, reason: reason || undefined }),
    onSuccess: () => {
      toast.success('Leave requested');
      void queryClient.invalidateQueries({ queryKey: ['leave'] });
      navigate(returnTo, { replace: true });
    },
    onError: (caught) => setError(describeError(caught)),
  });

  const dirty = !request.isSuccess && (Boolean(chosenEmployeeId) || type !== 'casual' || reason !== '' || fromDate !== todayIso() || toDate !== todayIso());

  return (
    <FormScreen
      title="Request leave"
      icon={CalendarOff}
      backTo={returnTo}
      dirty={dirty}
      onSubmit={(event) => {
        event.preventDefault();
        if (!employeeId) return;
        setError(null);
        request.mutate();
      }}
    >
      <FormSection title={tr('admin', 'person')} icon={UserRound} columns={1}>
        <Field label="Person" required>
          <Select value={employeeId} onChange={(e) => setEmployeeId(e.target.value)}>
            <option value="">Select…</option>
            {employees.data?.map((employee) => (
              <option key={employee.id} value={employee.id}>
                {employee.name}
              </option>
            ))}
          </Select>
        </Field>
      </FormSection>

      <FormSection title={tr('admin', 'leave')} icon={CalendarOff} columns={3}>
        <Field label="Type">
          <Select value={type} onChange={(e) => setType(e.target.value)}>
            {['casual', 'sick', 'annual', 'unpaid', 'maternity', 'paternity'].map((t) => (
              <option key={t} value={t}>
                {humanize(t)}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="From" required>
          <DatePicker value={fromDate} onChange={setFromDate} />
        </Field>
        <Field label="To" required>
          <DatePicker value={toDate} onChange={setToDate} />
        </Field>
        <Field label="Reason" className="sm:col-span-2 lg:col-span-3">
          <Textarea rows={2} value={reason} onChange={(e) => setReason(e.target.value)} />
        </Field>
      </FormSection>

      {error && <Alert variant="destructive">{error}</Alert>}

      <FormActions submitLabel="Request" loading={request.isPending} incomplete={!employeeId} cancelTo={returnTo} />
    </FormScreen>
  );
}
